/**
 * One pay run: the payslip table, the payslip detail, and finalising.
 *
 * Finalise is deliberately a wall. The API refuses with 422
 * `tax_table_unverified` while the financial year's ATO coefficients are still
 * marked unverified, and that refusal is shown here in full rather than as a
 * generic failure, because it is not a bug: it is the control that stops real
 * money going out against numbers nobody has checked against NAT 1004.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'wouter';
import { AlertTriangle, ArrowLeft, Lock, Printer, ShieldAlert } from 'lucide-react';
import { api, ApiError, qk, type Payslip } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { fmtDateRange, fmtDateTime12, fmtMoney } from '../../lib/time.ts';
import { Avatar, EmptyState, ErrorState, LoadingBlock, Spinner, TweenMoney } from '../../components/Bits.tsx';
import { Drawer, Modal, OutcomeModal } from '../../components/Overlay.tsx';
import { PayslipPrintArea, PayslipSheet } from '../../components/PayslipSheet.tsx';

const n = (v: string | number | null | undefined) => {
  const x = typeof v === 'string' ? Number(v) : (v ?? 0);
  return Number.isFinite(x) ? x : 0;
};

export default function PayRunPage() {
  const params = useParams<{ id: string }>();
  const { tenant, isOwner } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();
  const runId = params.id ?? '';

  const [open, setOpen] = useState<Payslip | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<{ ok: boolean; title: string; message: string } | null>(null);

  const query = useQuery({
    queryKey: qk.payRun(slug, runId),
    queryFn: () => api.payRun(slug, runId),
    enabled: slug !== '' && runId !== '',
  });

  const finalise = useMutation({
    mutationFn: () => api.finalisePayRun(slug, runId),
    onSuccess: () => {
      setConfirming(false);
      void qc.invalidateQueries({ queryKey: qk.payRun(slug, runId) });
      void qc.invalidateQueries({ queryKey: qk.payRuns(slug) });
      setOutcome({
        ok: true,
        title: 'Pay run finalised',
        message: 'Payslips are now visible to the team and everybody has been notified.',
      });
    },
    onError: (err) => {
      setConfirming(false);
      const e = err instanceof ApiError ? err : null;
      if (e?.code === 'tax_table_unverified') {
        setBlocked(e.message);
        return;
      }
      setOutcome({
        ok: false,
        title: 'Could not finalise',
        message: e?.message ?? 'Something went wrong. Nothing was finalised.',
      });
    },
  });

  const totals = useMemo(() => {
    const slips = query.data?.payslips ?? [];
    return slips.reduce(
      (acc, s) => ({
        gross: acc.gross + n(s.gross),
        tax: acc.tax + n(s.tax),
        net: acc.net + n(s.net),
        superAmount: acc.superAmount + n(s.superAmount),
      }),
      { gross: 0, tax: 0, net: 0, superAmount: 0 },
    );
  }, [query.data?.payslips]);

  if (query.isLoading) return <LoadingBlock label="Loading the pay run" />;
  if (query.isError || !query.data) {
    return <ErrorState error={query.error} what="this pay run" onRetry={() => void query.refetch()} />;
  }

  const run = query.data;
  const payslips = query.data.payslips ?? [];
  const finalised = run.status === 'finalised';
  const nameFor = (s: Payslip) => s.userName ?? 'Team member';

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <Link href="/pay" className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted">
        <ArrowLeft size={14} aria-hidden="true" />
        All pay runs
      </Link>

      <header className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-extrabold tracking-tight text-ink">
            {fmtDateRange(run.periodStart, run.periodEnd)}
          </h1>
          <p className="text-[13px] text-muted">
            {finalised ? `Finalised ${fmtDateTime12(run.finalisedAt)}` : 'Draft. Nothing has been sent to the team.'}
          </p>
        </div>
        {finalised ? (
          <span className="chip chip-accent" style={{ height: 30, padding: '0 12px' }}>
            <Lock size={13} aria-hidden="true" />
            Finalised
          </span>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isOwner || payslips.length === 0 || finalise.isPending}
            onClick={() => setConfirming(true)}
            data-tip={
              !isOwner
                ? 'Owner only|Finalising a pay run is the owner’s call. Ask them to run it.'
                : payslips.length === 0
                  ? 'Nothing to finalise|This run has no payslips on it.'
                  : 'Finalise this run|Payslips become visible to the team and the figures are locked.'
            }
          >
            {finalise.isPending ? <Spinner size={15} label="Finalising" /> : <Lock size={15} aria-hidden="true" />}
            Finalise
          </button>
        )}
      </header>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Gross', value: totals.gross, tone: 'var(--sp-text)', rule: 'var(--sp-border-strong)' },
          { label: 'Tax withheld', value: totals.tax, tone: 'var(--sp-warn-strong)', rule: 'var(--sp-warn)' },
          { label: 'Net paid', value: totals.net, tone: 'var(--sp-accent-strong)', rule: 'var(--sp-accent)' },
          { label: 'Super', value: totals.superAmount, tone: 'var(--sp-text)', rule: 'var(--sp-border-strong)' },
        ].map((s, i) => (
          <div
            key={s.label}
            className="card anim-rise p-3.5"
            style={{ animationDelay: `${i * 50}ms`, borderTop: `3px solid ${s.rule}` }}
          >
            <div className="text-[11px] font-bold uppercase tracking-wide text-faint">{s.label}</div>
            <div className="tabular text-[19px] font-extrabold" style={{ color: s.tone }}>
              <TweenMoney value={s.value} />
            </div>
          </div>
        ))}
      </div>

      {payslips.length === 0 ? (
        <EmptyState
          title="No payslips on this run"
          body="Nobody had approved timesheets in this period. Approve the week's timesheets, then create the run again."
        />
      ) : (
        <div className="card overflow-hidden">
          <div
            className="grid px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-faint"
            style={{
              gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr 44px',
              background: 'var(--sp-surface-2)',
              borderBottom: '1px solid var(--sp-border)',
            }}
          >
            <span>Who</span>
            <span className="text-right">Gross</span>
            <span className="text-right">Tax</span>
            <span className="text-right">Net</span>
            <span className="text-right">Super</span>
            <span />
          </div>
          {payslips.map((s, i) => (
            <div
              key={s.id}
              className="grid items-center px-4 py-2.5"
              style={{
                gridTemplateColumns: '1.8fr 1fr 1fr 1fr 1fr 44px',
                borderTop: i > 0 ? '1px solid var(--sp-hairline)' : undefined,
              }}
            >
              <span className="flex min-w-0 items-center gap-2">
                <Avatar name={nameFor(s)} size={28} />
                <span className="truncate text-[13.5px] font-semibold text-ink">{nameFor(s)}</span>
              </span>
              <span className="tabular text-right text-[13.5px] text-ink">{fmtMoney(s.gross)}</span>
              <span className="tabular text-right text-[13.5px]" style={{ color: 'var(--sp-warn-strong)' }}>
                {fmtMoney(s.tax)}
              </span>
              <span className="tabular text-right text-[13.5px] font-bold text-ink">{fmtMoney(s.net)}</span>
              <span className="tabular text-right text-[13.5px] text-muted">{fmtMoney(s.superAmount)}</span>
              <span className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-icon btn-sm"
                  onClick={() => setOpen(s)}
                  title={`Open ${nameFor(s)}'s payslip|Itemised lines, year to date figures and a printable copy.`}
                  aria-label={`Open the payslip for ${nameFor(s)}`}
                >
                  <Printer size={14} aria-hidden="true" />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? `${nameFor(open)}'s payslip` : 'Payslip'}
        subtitle={fmtDateRange(run.periodStart, run.periodEnd)}
        width={760}
        footer={
          <button type="button" className="btn btn-primary" onClick={() => window.print()}>
            <Printer size={15} aria-hidden="true" />
            Print
          </button>
        }
      >
        {open ? (
          <PayslipSheet
            slip={{
              ...open,
              periodStart: open.periodStart ?? run.periodStart,
              periodEnd: open.periodEnd ?? run.periodEnd,
              issuedAt: open.issuedAt ?? run.finalisedAt ?? open.createdAt,
              tenantName: open.tenantName ?? tenant?.tenantName,
            }}
            staffName={nameFor(open)}
          />
        ) : null}
      </Drawer>

      <PayslipPrintArea
        slip={
          open
            ? {
                ...open,
                periodStart: open.periodStart ?? run.periodStart,
                periodEnd: open.periodEnd ?? run.periodEnd,
                issuedAt: open.issuedAt ?? run.finalisedAt ?? open.createdAt,
                tenantName: open.tenantName ?? tenant?.tenantName,
              }
            : null
        }
        staffName={open ? nameFor(open) : ''}
      />

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Finalise this pay run?"
        subtitle={fmtDateRange(run.periodStart, run.periodEnd)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirming(false)}>
              Not yet
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => finalise.mutate()}
              disabled={finalise.isPending}
            >
              <Lock size={15} aria-hidden="true" />
              Finalise and notify
            </button>
          </>
        }
      >
        <p className="text-[13.5px] leading-relaxed text-muted">
          {payslips.length} {payslips.length === 1 ? 'payslip becomes' : 'payslips become'} visible to the team, and
          the figures are locked. Total net pay is{' '}
          <strong className="tabular text-ink">{fmtMoney(totals.net)}</strong>, with{' '}
          <strong className="tabular text-ink">{fmtMoney(totals.tax)}</strong> withheld and{' '}
          <strong className="tabular text-ink">{fmtMoney(totals.superAmount)}</strong> of super owing.
        </p>
        <div
          className="mt-4 flex gap-2.5 rounded-card border p-3.5"
          style={{ background: 'var(--sp-caution-soft)', borderColor: 'var(--sp-caution)' }}
        >
          <AlertTriangle size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
          <p className="text-[13px] leading-relaxed text-ink">
            Check the figures before you finalise. Correcting a finalised run means a fresh adjustment, not an edit.
          </p>
        </div>
      </Modal>

      {/* The tax table refusal gets its own dialog rather than a generic error,
          because it is a deliberate safety catch and the reader needs to know
          exactly what to do about it. */}
      <Modal
        open={blocked !== null}
        onClose={() => setBlocked(null)}
        title="The tax tables have not been verified"
        footer={
          <button type="button" className="btn btn-primary" onClick={() => setBlocked(null)}>
            Understood
          </button>
        }
      >
        <div
          className="flex gap-2.5 rounded-card border p-3.5"
          style={{ background: 'var(--sp-warn-soft)', borderColor: 'var(--sp-warn-border)' }}
        >
          <ShieldAlert size={17} aria-hidden="true" className="mt-0.5 shrink-0" style={{ color: 'var(--sp-warn-strong)' }} />
          <p className="text-[13.5px] leading-relaxed text-ink">{blocked}</p>
        </div>
        <p className="mt-4 text-[13.5px] leading-relaxed text-muted">
          Sneaky Panda will not finalise a pay run while the withholding coefficients for that financial year are
          still unchecked. The figures on screen are calculated and correct arithmetically; what has not happened is
          somebody confirming the coefficients against the current ATO schedule. Until that is done, no real money
          should go out against them.
        </p>
      </Modal>

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.title ?? ''}
        message={outcome?.message ?? ''}
      />
    </div>
  );
}
